import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { DEFAULT_INVOICE_LANGUAGE } from '@features/invoices/constants/defaultInvoiceLanguage'
import { DEFAULT_INVOICE_PREFIX } from '@features/invoices/constants/defaultInvoicePrefix'
import { defaultInvoiceTextTranslationKeys } from '@features/invoices/constants/defaultInvoiceTextTranslationKeys'
import { invoiceLanguageToLocale } from '@features/invoices/constants/invoiceLanguageToLocale'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { getDefaultInvoiceLocale } from '@features/invoices/utils/getDefaultInvoiceLocale'
import { useGetZervantDocumentSettings } from '@features/invoicingSettings/hooks/useGetZervantDocumentSettings'
import { useZervantSettings } from '@features/invoicingSettings/hooks/useZervantSettings'
import { ProductType } from '@features/products/enums/productType'
import { getConfiguredProductUnits } from '@features/products/utils/getConfiguredProductUnits'
import { getDefaultProductUnitName } from '@features/products/utils/getDefaultProductUnitName'
import { useFilteredVatRates } from '@features/vatRates/hooks/useFilteredVatRates'
import { useGetVatRate } from '@features/vatRates/hooks/useGetVatRate'

import { NumberValue } from '../../../../../utils'
import { useFetchNextQuoteNumber } from '../../../hooks/useFetchNextQuoteNumber'
import { useInvoiceNumberingSequences } from '../../../hooks/useInvoiceNumberingSequences'
import { useInvoiceEditorForms } from '../contexts/invoiceEditorFormsContext'
import { PredefinedInvoiceValues } from '../types/predefinedInvoiceValues'
import { useInvoiceEditorDocumentType } from './useInvoiceEditorDocumentType'

export const usePredefinedInvoiceValues = () => {
  const { t } = useTranslation()
  const { data: zervantSettings, isLoading: isLoadingZervantSettings } = useZervantSettings()
  const { invoiceNumberingSequences, isLoading: isLoadingInvoicesNumbering } = useInvoiceNumberingSequences()
  const { nextQuoteNumber, isLoading: isLoadingNextQuoteNumber } = useFetchNextQuoteNumber()
  const { isLoading: isLoadingFilteredVatRates, getFilteredVatRates } = useFilteredVatRates()
  const { documentType } = useInvoiceEditorDocumentType()
  // we're using useInvoiceEditorForms and not useInvoiceEditorForm in this case,
  // because usePredefinedInvoiceValues itself is actually used in useInvoiceEditorForm
  const { invoiceEditorForm } = useInvoiceEditorForms()

  const { getDefaultSettingsValue, isLoading: isLoadingZervantDocumentSettings } = useGetZervantDocumentSettings(
    InvoiceDocumentType.Invoice,
  )
  const { getVatRate } = useGetVatRate()

  const isLoading =
    isLoadingNextQuoteNumber ||
    isLoadingFilteredVatRates ||
    isLoadingInvoicesNumbering ||
    isLoadingZervantDocumentSettings ||
    isLoadingZervantSettings

  const isQuote = documentType === InvoiceDocumentType.Quote

  const defaultProductType = useMemo(
    () => getDefaultSettingsValue('productType') as ProductType | undefined,
    [getDefaultSettingsValue],
  )

  const selectedContact = invoiceEditorForm.watch('receiver')

  const vatRates = useMemo(
    () =>
      getFilteredVatRates({
        productType: defaultProductType,
        ...(selectedContact
          ? {
              contactCountryCode: selectedContact?.countryId,
              contactType: selectedContact?.type,
            }
          : {}),
      }),
    [defaultProductType, getFilteredVatRates, selectedContact],
  )

  const defaultVatRate = useMemo(
    () => getVatRate({ vatRateId: zervantSettings?.values.company.vatRateId, vatRates }),
    [getVatRate, vatRates, zervantSettings?.values.company.vatRateId],
  )

  const defaultUnit = useMemo(
    () => getDefaultProductUnitName(getConfiguredProductUnits(zervantSettings?.managedFields?.UNIT_OPTIONS, t)),
    [t, zervantSettings?.managedFields?.UNIT_OPTIONS],
  )

  const activeInvoiceNumberingSequence = useMemo(
    () => invoiceNumberingSequences?.find((sequence) => sequence.active),
    [invoiceNumberingSequences],
  )

  const invoiceNumber = useMemo(
    () => (isQuote ? nextQuoteNumber : activeInvoiceNumberingSequence?.next),
    [activeInvoiceNumberingSequence?.next, isQuote, nextQuoteNumber],
  )

  const invoiceSettingsLanguage = getDefaultSettingsValue('language')
  const defaultLanguage = useMemo(() => getDefaultInvoiceLocale(invoiceSettingsLanguage), [invoiceSettingsLanguage])

  const defaultMessage = useMemo(
    () =>
      getDefaultSettingsValue('description') ??
      t(defaultInvoiceTextTranslationKeys.message, {
        lng: defaultLanguage ?? invoiceLanguageToLocale[DEFAULT_INVOICE_LANGUAGE],
      }),
    [defaultLanguage, getDefaultSettingsValue, t],
  )

  const defaultFooterNote = useMemo(
    () =>
      getDefaultSettingsValue('footerText') ??
      t(defaultInvoiceTextTranslationKeys.footerNote, {
        lng: defaultLanguage ?? invoiceLanguageToLocale[DEFAULT_INVOICE_LANGUAGE],
      }),
    [defaultLanguage, getDefaultSettingsValue, t],
  )

  const predefinedInvoiceValues: PredefinedInvoiceValues = useMemo(() => {
    return {
      currency: getDefaultSettingsValue('currency'),
      footerNote: defaultFooterNote,
      invoiceNumber,
      ...(isQuote ? {} : { invoiceNumberPrefix: activeInvoiceNumberingSequence?.prefix || DEFAULT_INVOICE_PREFIX }),
      language: defaultLanguage,
      message: defaultMessage,
      paymentTerm: new NumberValue(getDefaultSettingsValue('paymentTime')).getValue(),
      productType: defaultProductType,
      unit: defaultUnit,
      vatRateId: defaultVatRate?.id,
      vatRatePercent: defaultVatRate?.rate || 0,
    }
  }, [
    activeInvoiceNumberingSequence?.prefix,
    defaultLanguage,
    defaultMessage,
    defaultFooterNote,
    defaultProductType,
    defaultUnit,
    defaultVatRate?.id,
    defaultVatRate?.rate,
    getDefaultSettingsValue,
    invoiceNumber,
    isQuote,
  ])

  return { predefinedInvoiceValues, isLoading }
}
